import React, { useState, useEffect, useCallback, useRef } from 'react';
import AdminLayout from '@/components/layouts/AdminLayout';
import { API, useAuth } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Plus, Pencil, Trash2, Eye, EyeOff, Save, X, ExternalLink, Search,
  Image, Upload, Link2, Loader2
} from 'lucide-react';

const GRADIENTS = [
  { label: 'Indigo → Cyan', value: 'from-indigo-600/20 to-cyan-600/20' },
  { label: 'Emerald → Teal', value: 'from-emerald-600/20 to-teal-600/20' },
  { label: 'Amber → Orange', value: 'from-amber-600/20 to-orange-600/20' },
  { label: 'Purple → Pink', value: 'from-purple-600/20 to-pink-600/20' },
  { label: 'Sky → Blue', value: 'from-sky-600/20 to-blue-600/20' },
  { label: 'Rose → Red', value: 'from-rose-600/20 to-red-600/20' },
];

const emptyForm = { title: '', slug: '', excerpt: '', category: '', read_time: '5 min read', cover_gradient: GRADIENTS[0].value, cover_image: '', content: '', is_published: true, seo_title: '', meta_description: '', canonical_url: '' };

// ─── Inline Image Inserter toolbar ────────────────────────────────────────────
function ImageInsertToolbar({ onInsert, token }) {
  const [open, setOpen] = useState(false);
  const [mode, setMode] = useState('url'); // 'url' | 'upload'
  const [url, setUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const doInsert = (imgUrl) => {
    if (!imgUrl) return;
    onInsert(`<img src="${imgUrl}" alt="" style="max-width:100%;height:auto;border-radius:8px;margin:16px 0;" />`);
    setUrl('');
    setOpen(false);
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/blogs/admin/upload-image`, fd, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' },
      });
      doInsert(`${process.env.REACT_APP_BACKEND_URL}${data.url}`);
    } catch {
      toast.error('Image upload failed');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="flex items-center gap-1.5 h-8 px-3 text-xs font-medium rounded-md border border-border bg-muted/40 hover:bg-muted text-foreground transition-colors"
        data-testid="insert-image-btn"
      >
        <Image className="h-3.5 w-3.5" /> Insert Image
      </button>
      {open && (
        <div className="absolute left-0 top-10 z-20 w-80 bg-card border border-border rounded-xl shadow-lg p-4 space-y-3">
          <div className="flex gap-1 mb-1">
            {['url', 'upload'].map(m => (
              <button key={m} type="button" onClick={() => setMode(m)}
                className={`flex-1 h-7 rounded text-xs font-medium transition-colors ${mode === m ? 'bg-primary text-primary-foreground' : 'bg-muted text-muted-foreground hover:text-foreground'}`}>
                {m === 'url' ? 'Paste URL' : 'Upload File'}
              </button>
            ))}
          </div>
          {mode === 'url' ? (
            <div className="flex gap-2">
              <input value={url} onChange={e => setUrl(e.target.value)} placeholder="https://example.com/image.jpg"
                className="flex-1 h-8 px-2 text-xs rounded border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary/40"
                data-testid="insert-image-url-input"
              />
              <Button size="sm" className="h-8 px-3 text-xs" onClick={() => doInsert(url)}>Insert</Button>
            </div>
          ) : (
            <div>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={handleUpload} />
              <Button size="sm" className="w-full h-8 text-xs" onClick={() => fileRef.current?.click()} disabled={uploading}>
                {uploading ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> Uploading...</> : <><Upload className="h-3.5 w-3.5 mr-1.5" /> Choose Image</>}
              </Button>
            </div>
          )}
          <button type="button" onClick={() => setOpen(false)} className="absolute top-2 right-2 p-1 text-muted-foreground hover:text-foreground">
            <X className="h-3.5 w-3.5" />
          </button>
        </div>
      )}
    </div>
  );
}

const AdminBlogs = () => {
  const { token } = useAuth();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = list, 'new' = create, blog_id = edit
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [coverUploading, setCoverUploading] = useState(false);
  const contentRef = useRef(null);
  const coverFileRef = useRef(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchBlogs = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/blogs/admin`, { headers });
      setBlogs(data);
    } catch (err) {
      toast.error('Failed to load blogs');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { fetchBlogs(); }, [fetchBlogs]);

  const slugify = (text) => text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

  const handleTitleChange = (val) => {
    setForm(f => ({ ...f, title: val, slug: editing === 'new' ? slugify(val) : f.slug }));
  };

  const startCreate = () => {
    setForm(emptyForm);
    setEditing('new');
  };

  const startEdit = (blog) => {
    setForm({
      title: blog.title,
      slug: blog.slug,
      excerpt: blog.excerpt,
      category: blog.category,
      read_time: blog.read_time,
      cover_gradient: blog.cover_gradient,
      cover_image: blog.cover_image || '',
      content: blog.content || '',
      is_published: blog.is_published,
      seo_title: blog.seo_title || '',
      meta_description: blog.meta_description || '',
      canonical_url: blog.canonical_url || '',
    });
    setEditing(blog.id);
  };

  const handleSave = async () => {
    if (!form.title || !form.slug || !form.excerpt || !form.category) {
      toast.error('Please fill in title, slug, excerpt, and category');
      return;
    }
    setSaving(true);
    try {
      if (editing === 'new') {
        await axios.post(`${API}/blogs/admin`, form, { headers });
        toast.success('Blog post created');
      } else {
        await axios.put(`${API}/blogs/admin/${editing}`, form, { headers });
        toast.success('Blog post updated');
      }
      setEditing(null);
      fetchBlogs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this blog post? This cannot be undone.')) return;
    try {
      await axios.delete(`${API}/blogs/admin/${id}`, { headers });
      toast.success('Blog post deleted');
      fetchBlogs();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const togglePublish = async (blog) => {
    try {
      await axios.put(`${API}/blogs/admin/${blog.id}`, { is_published: !blog.is_published }, { headers });
      toast.success(blog.is_published ? 'Post unpublished' : 'Post published');
      fetchBlogs();
    } catch (err) {
      toast.error('Update failed');
    }
  };

  // Upload cover image file
  const handleCoverUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCoverUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API}/blogs/admin/upload-image`, fd, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });
      setForm(f => ({ ...f, cover_image: `${process.env.REACT_APP_BACKEND_URL}${data.url}` }));
      toast.success('Cover image uploaded');
    } catch {
      toast.error('Cover image upload failed');
    } finally {
      setCoverUploading(false);
      e.target.value = '';
    }
  };

  // Insert text at cursor in content textarea
  const insertAtCursor = (text) => {
    const el = contentRef.current;
    if (!el) return;
    const start = el.selectionStart;
    const end = el.selectionEnd;
    const newContent = form.content.substring(0, start) + text + form.content.substring(end);
    setForm(f => ({ ...f, content: newContent }));
    // Restore cursor after inserted text
    setTimeout(() => {
      el.focus();
      el.setSelectionRange(start + text.length, start + text.length);
    }, 0);
  };

  // =============== EDITOR VIEW ===============
  if (editing) {
    return (
      <AdminLayout title={editing === 'new' ? 'New Blog Post' : 'Edit Blog Post'}>
        <div className="max-w-4xl" data-testid="blog-editor">
          <div className="flex items-center justify-between mb-6">
            <Button variant="ghost" size="sm" onClick={() => setEditing(null)} data-testid="blog-editor-cancel">
              <X className="h-4 w-4 mr-1.5" /> Cancel
            </Button>
            <Button size="sm" onClick={handleSave} disabled={saving} data-testid="blog-editor-save">
              <Save className="h-4 w-4 mr-1.5" /> {saving ? 'Saving...' : 'Save Post'}
            </Button>
          </div>

          <div className="space-y-5">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Title</label>
              <input
                type="text" value={form.title} onChange={e => handleTitleChange(e.target.value)}
                className="w-full h-10 px-3 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                placeholder="Blog post title" data-testid="blog-input-title"
              />
            </div>

            {/* Slug */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">URL Slug</label>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">/blogs/</span>
                <input
                  type="text" value={form.slug} onChange={e => setForm(f => ({ ...f, slug: e.target.value }))}
                  className="flex-1 h-10 px-3 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 font-mono"
                  placeholder="url-friendly-slug" data-testid="blog-input-slug"
                />
              </div>
            </div>

            {/* Cover Image */}
            <div className="rounded-xl border border-border p-5 space-y-3 bg-muted/20">
              <div className="flex items-center gap-2 mb-1">
                <Image className="h-4 w-4 text-primary" />
                <span className="text-sm font-semibold text-foreground">Cover Image</span>
                <span className="text-xs text-muted-foreground">(optional — overrides gradient)</span>
              </div>

              {/* Preview */}
              {form.cover_image ? (
                <div className="relative rounded-lg overflow-hidden border border-border h-40">
                  <img src={form.cover_image} alt="Cover" className="w-full h-full object-cover" />
                  <button
                    type="button"
                    onClick={() => setForm(f => ({ ...f, cover_image: '' }))}
                    className="absolute top-2 right-2 p-1 rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors"
                    data-testid="remove-cover-image"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              ) : (
                <div className={`rounded-lg h-20 bg-gradient-to-br ${form.cover_gradient} border border-dashed border-border flex items-center justify-center text-xs text-muted-foreground`}>
                  No cover image — gradient will be used
                </div>
              )}

              {/* URL input + upload */}
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <Link2 className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-muted-foreground" />
                  <input
                    type="text"
                    value={form.cover_image}
                    onChange={e => setForm(f => ({ ...f, cover_image: e.target.value }))}
                    placeholder="Paste image URL or upload below"
                    className="w-full h-9 pl-8 pr-3 text-sm rounded-lg border border-border bg-background focus:outline-none focus:ring-1 focus:ring-primary/40"
                    data-testid="cover-image-url-input"
                  />
                </div>
                <input ref={coverFileRef} type="file" accept="image/*" className="hidden" onChange={handleCoverUpload} />
                <Button
                  type="button" variant="outline" size="sm" className="h-9 px-3 text-xs"
                  onClick={() => coverFileRef.current?.click()} disabled={coverUploading}
                  data-testid="upload-cover-btn"
                >
                  {coverUploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <><Upload className="h-3.5 w-3.5 mr-1" /> Upload</>}
                </Button>
              </div>
            </div>

            {/* SEO Section */}
            <div className="rounded-xl border border-border p-5 space-y-4 bg-muted/20">
              <div className="flex items-center gap-2 mb-1">
                <Search className="h-4 w-4 text-primary" />
                <span className="text-sm font-semibold text-foreground">SEO Settings</span>
              </div>

              {/* SEO Title */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm font-medium text-foreground">SEO Title</label>
                  <span className={`text-xs ${form.seo_title.length > 65 ? 'text-red-400' : form.seo_title.length > 55 ? 'text-amber-400' : 'text-muted-foreground'}`}>{form.seo_title.length}/65</span>
                </div>
                <input
                  type="text" value={form.seo_title} onChange={e => setForm(f => ({ ...f, seo_title: e.target.value }))}
                  className="w-full h-10 px-3 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  placeholder="Custom title for search engines (defaults to post title)" data-testid="blog-input-seo-title"
                />
                <p className="text-xs text-muted-foreground mt-1">Recommended: 60–65 characters for optimal display in search results</p>
              </div>

              {/* Meta Description */}
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="text-sm font-medium text-foreground">Meta Description</label>
                  <span className={`text-xs ${form.meta_description.length > 160 ? 'text-red-400' : form.meta_description.length > 145 ? 'text-amber-400' : 'text-muted-foreground'}`}>{form.meta_description.length}/160</span>
                </div>
                <textarea
                  value={form.meta_description} onChange={e => setForm(f => ({ ...f, meta_description: e.target.value }))}
                  className="w-full h-16 px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                  placeholder="Summary for search engine snippet" data-testid="blog-input-meta-description"
                />
                <p className="text-xs text-muted-foreground mt-1">Recommended: 150–160 characters. This appears below the title in Google results</p>
              </div>

              {/* Canonical URL */}
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Canonical URL <span className="text-muted-foreground font-normal">(optional)</span></label>
                <input
                  type="text" value={form.canonical_url} onChange={e => setForm(f => ({ ...f, canonical_url: e.target.value }))}
                  className="w-full h-10 px-3 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 font-mono"
                  placeholder="https://coinpayup.com/blogs/original-post" data-testid="blog-input-canonical-url"
                />
                <p className="text-xs text-muted-foreground mt-1">Set if this post is republished from another URL to prevent duplicate content issues</p>
              </div>

              {/* SEO Preview */}
              {(form.seo_title || form.title) && (
                <div className="mt-3 p-3 rounded-lg bg-background border border-border">
                  <p className="text-xs text-muted-foreground mb-2 font-medium">Google Preview</p>
                  <p className="text-[#1a0dab] text-sm font-medium truncate" style={{ fontFamily: 'Arial, sans-serif' }}>{form.seo_title || form.title}</p>
                  <p className="text-emerald-600 text-xs mt-0.5 truncate" style={{ fontFamily: 'Arial, sans-serif' }}>coinpayup.com/blogs/{form.slug || '...'}</p>
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2" style={{ fontFamily: 'Arial, sans-serif' }}>{form.meta_description || form.excerpt || 'No description set'}</p>
                </div>
              )}
            </div>

            {/* Excerpt */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">Excerpt</label>
              <textarea
                value={form.excerpt} onChange={e => setForm(f => ({ ...f, excerpt: e.target.value }))}
                className="w-full h-20 px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30 resize-none"
                placeholder="Short description for the listing page" data-testid="blog-input-excerpt"
              />
            </div>

            {/* Category + Read Time row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Category</label>
                <input
                  type="text" value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                  className="w-full h-10 px-3 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  placeholder="e.g. Payment Security" data-testid="blog-input-category"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1.5">Read Time</label>
                <input
                  type="text" value={form.read_time} onChange={e => setForm(f => ({ ...f, read_time: e.target.value }))}
                  className="w-full h-10 px-3 rounded-lg border border-border bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                  placeholder="e.g. 5 min read" data-testid="blog-input-readtime"
                />
              </div>
            </div>

            {/* Cover Gradient (used when no cover_image) */}
            <div>
              <label className="block text-sm font-medium text-foreground mb-1.5">
                Fallback Gradient <span className="text-muted-foreground font-normal text-xs">(used when no cover image set)</span>
              </label>
              <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                {GRADIENTS.map(g => (
                  <button key={g.value} onClick={() => setForm(f => ({ ...f, cover_gradient: g.value }))}
                    className={`h-10 rounded-lg bg-gradient-to-br ${g.value} border-2 transition-all ${form.cover_gradient === g.value ? 'border-primary ring-2 ring-primary/30' : 'border-border'}`}
                    title={g.label} data-testid={`gradient-${g.label}`}
                  />
                ))}
              </div>
            </div>

            {/* Published toggle */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => setForm(f => ({ ...f, is_published: !f.is_published }))}
                className={`w-10 h-6 rounded-full relative transition-colors ${form.is_published ? 'bg-emerald-500' : 'bg-muted'}`}
                data-testid="blog-toggle-published"
              >
                <div className={`w-4 h-4 rounded-full bg-white absolute top-1 transition-transform ${form.is_published ? 'translate-x-5' : 'translate-x-1'}`} />
              </button>
              <span className="text-sm text-foreground">{form.is_published ? 'Published' : 'Draft'}</span>
            </div>

            {/* Content (HTML) */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-sm font-medium text-foreground">Content (HTML)</label>
                <ImageInsertToolbar onInsert={insertAtCursor} token={token} />
              </div>
              <textarea
                ref={contentRef}
                value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))}
                className="w-full h-96 px-4 py-3 rounded-lg border border-border bg-background text-foreground text-sm font-mono focus:outline-none focus:ring-2 focus:ring-primary/30 resize-y"
                placeholder="Write your blog content in HTML format..." data-testid="blog-input-content"
              />
              <p className="text-xs text-muted-foreground mt-1.5">
                Use HTML tags: &lt;h2&gt;, &lt;h3&gt;, &lt;p&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;strong&gt;, &lt;code&gt;, &lt;img&gt;, etc.
              </p>
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  // =============== LIST VIEW ===============
  return (
    <AdminLayout title="Blog Management">
      <div data-testid="admin-blogs-page">
        <div className="flex items-center justify-between mb-6">
          <p className="text-sm text-muted-foreground">{blogs.length} blog post{blogs.length !== 1 ? 's' : ''}</p>
          <Button size="sm" onClick={startCreate} data-testid="blog-create-btn">
            <Plus className="h-4 w-4 mr-1.5" /> New Post
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground text-sm">Loading...</div>
        ) : blogs.length === 0 ? (
          <div className="text-center py-16 border border-dashed border-border rounded-xl">
            <p className="text-muted-foreground text-sm mb-3">No blog posts yet</p>
            <Button size="sm" onClick={startCreate}><Plus className="h-4 w-4 mr-1.5" /> Create your first post</Button>
          </div>
        ) : (
          <div className="space-y-3">
            {blogs.map(blog => (
              <div key={blog.id} className="flex items-center gap-4 p-4 rounded-xl border border-border bg-card hover:border-primary/20 transition-colors" data-testid={`blog-row-${blog.id}`}>
                {/* Cover preview */}
                <div className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 border border-border">
                  {blog.cover_image ? (
                    <img src={blog.cover_image} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className={`w-full h-full bg-gradient-to-br ${blog.cover_gradient}`} />
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-sm font-semibold text-foreground truncate">{blog.title}</h3>
                    <span className={`px-1.5 py-0.5 text-[10px] font-medium rounded-full ${blog.is_published ? 'bg-emerald-500/10 text-emerald-400' : 'bg-muted text-muted-foreground'}`}>
                      {blog.is_published ? 'Published' : 'Draft'}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground truncate">/blogs/{blog.slug}</p>
                </div>

                {/* Category */}
                <span className="hidden sm:inline text-xs text-muted-foreground bg-muted px-2 py-1 rounded-full">{blog.category}</span>

                {/* Actions */}
                <div className="flex items-center gap-1 flex-shrink-0">
                  <button onClick={() => togglePublish(blog)} className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground" title={blog.is_published ? 'Unpublish' : 'Publish'} data-testid={`blog-toggle-${blog.id}`}>
                    {blog.is_published ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                  <button onClick={() => startEdit(blog)} className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground" title="Edit" data-testid={`blog-edit-${blog.id}`}>
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button onClick={() => handleDelete(blog.id)} className="p-2 rounded-lg hover:bg-red-500/10 transition-colors text-muted-foreground hover:text-red-500" title="Delete" data-testid={`blog-delete-${blog.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </button>
                  {blog.is_published && (
                    <a href={`/blogs/${blog.slug}`} target="_blank" rel="noopener noreferrer" className="p-2 rounded-lg hover:bg-muted transition-colors text-muted-foreground hover:text-foreground" title="View" data-testid={`blog-view-${blog.id}`}>
                      <ExternalLink className="h-4 w-4" />
                    </a>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminBlogs;
